import React, { useState, useRef, useEffect, memo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { PropertyCard } from "./components/PropertyCard";
import { VolumeHistogram } from "./components/VolumeHistogram";
import { BotAvatar } from "./components/BotAvatar";
import type { ChatProperty } from "./types";
import { Send, Mic, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// API Configuration
const CHAT_API = process.env.BUN_PUBLIC_CHAT_API_URL;
const CHAT_HEARTBEAT_INTERVAL = 30000; // 30 seconds

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  properties?: ChatProperty[];
  filters?: any;
}

interface ChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  messages: Message[];
  onSendMessage: (message: string) => void;
  onSendVoiceMessage: (audioBlob: Blob) => void;
  isLoading: boolean;
  embedded?: boolean;
}

const MessageItem = memo(({ message }: { message: Message }) => {
  return (
    <div
      className={`flex w-full ${
        message.isUser ? "flex-row-reverse" : "flex-row"
      }`}
    >
      <div className={`flex flex-col ${message.isUser ? "items-end" : "items-start"}`}>
        {!message.isUser && (
          <div className="mb-2 flex items-center gap-2">
            <BotAvatar />
            <span className="text-sm font-medium text-gray-600">AI Assistant</span>
            <span className="text-xs text-gray-400">
              {message.timestamp.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        )}
        
        <div
          className={`max-w-[280px] rounded-2xl px-4 py-3 shadow-md ${
            message.isUser
              ? "rounded-tr-sm bg-[#D14081] text-white"
              : "rounded-tl-sm bg-gray-100 text-gray-800"
          }`}
        >
          <div className="text-sm leading-relaxed">
            <ReactMarkdown>{message.text}</ReactMarkdown>
          </div>
        </div>

        {/* Show properties if this is a bot message with properties */}
        {!message.isUser &&
          message.properties &&
          message.properties.length > 0 && (
            <div className="mt-4 w-full space-y-4">
              {message.properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}
      </div>
    </div>
  );
});

const PLACEHOLDER_TEXTS = [
  "Do you have a villa up to 3 mln with 3br?",
  "Can I see a townhouse with a good sea view?",
  "A villa with infinity pool near a good private school?",
  "Show me apartments with parking in the city center",
  "Looking for a penthouse with mountain views",
  "Any properties with a gym and spa facilities?",
  "I need a 4-bedroom house with a large garden",
  "Beach front properties under 2 million?",
  "Modern apartments with smart home features?",
  "Family homes near international schools?",
];

const ChatWindow = ({
  isOpen,
  onClose,
  messages,
  onSendMessage,
  onSendVoiceMessage,
  isLoading,
  embedded = false,
}: ChatWindowProps) => {
  const [inputValue, setInputValue] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Animated placeholder state
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderText, setPlaceholderText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current && !isRecording) {
      inputRef.current.focus();
    }
  }, [isOpen, isRecording]);

  // Animated placeholder effect
  useEffect(() => {
    const currentFullText = PLACEHOLDER_TEXTS[placeholderIndex] || "";
    const timeout = setTimeout(
      () => {
        if (!isDeleting) {
          // Typing
          if (placeholderText.length < currentFullText.length) {
            setPlaceholderText(currentFullText.slice(0, placeholderText.length + 1));
          } else {
            // Finished typing, wait a bit then start deleting
            setTimeout(() => setIsDeleting(true), 2000);
          }
        } else {
          // Deleting
          if (placeholderText.length > 0) {
            setPlaceholderText(placeholderText.slice(0, -1));
          } else {
            // Finished deleting, move to next placeholder
            setIsDeleting(false);
            setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_TEXTS.length);
          }
        }
      },
      isDeleting ? 30 : 50 // Faster when deleting
    );

    return () => clearTimeout(timeout);
  }, [placeholderText, isDeleting, placeholderIndex]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading && !isRecording) {
      onSendMessage(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (isRecording) {
        stopRecording();
      } else if (!embedded) {
        onClose();
      }
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const audioChunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
        onSendVoiceMessage(audioBlob);
        stream.getTracks().forEach((track) => track.stop());
        setAudioStream(null);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setAudioStream(stream);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div
      className={`flex h-[600px] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ${
        embedded ? "" : "fixed bottom-5 right-5 z-50 max-w-[380px]"
      }`}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-label="Chat window"
      aria-modal="true"
    >
      {/* Header */}
      <div className="relative flex items-center justify-center bg-gradient-to-r from-[#D14081] to-[#2A1745] p-4 text-white shadow-md">
        <div className="flex items-center gap-2">
          <BotAvatar />
          <span className="font-semibold">AI Chat Assistant</span>
        </div>
        <button
          onClick={onClose}
          className="absolute right-2 top-2 rounded-lg p-2 transition-colors hover:bg-white/20"
          aria-label="Close chat"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4">
        {messages.length === 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <BotAvatar />
              <span className="text-sm font-medium text-gray-600">AI Assistant</span>
            </div>
            <div className="max-w-[280px] rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3 text-sm leading-relaxed text-gray-800 shadow-md">
              <p>
                Welcome! I'm your AI assistant. I can help you find what you're looking for. 
                How can I assist you today?
              </p>
              <p style={{ marginTop: 20, fontSize: 12, color: 'grey' }}>I am using a demo dataset of properties for sale in Marbella, Spain, and I can search the internet for better responses.</p>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <MessageItem key={message.id} message={message} />
        ))}

        {isLoading && (
          <div className="flex gap-3">
            <BotAvatar />
            <div className="rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3 shadow-md">
              <div className="flex gap-1">
                <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
                <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
                <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400" />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-gray-200 bg-white p-4 shadow-lg">
        <div className="flex items-center gap-2">
          {isRecording ? (
            <VolumeHistogram audioStream={audioStream} />
          ) : (
            <Input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={placeholderText}
              className="flex-1 !outline-none focus-visible:!ring-0 hover:!border-[#2A1745] hover:!shadow-[0_0_5px_rgba(42,23,69,0.5)] focus:!border-[#2A1745] focus:!shadow-[0_0_5px_rgba(42,23,69,0.5)] transition-all"
              disabled={isLoading}
              aria-label="Type your message"
            />
          )}

          {/* Voice button */}
          <Button
            type="button"
            onClick={toggleRecording}
            disabled={isLoading}
            variant={isRecording ? "destructive" : "outline"}
            size="icon"
            aria-label={isRecording ? "Stop recording" : "Start voice recording"}
          >
            <Mic className="h-4 w-4" />
          </Button>

          <Button
            type="submit"
            disabled={!inputValue.trim() || isLoading || isRecording}
            className="bg-[#D14081] hover:bg-[#D14081]/90"
            size="icon"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

interface ChatWidgetProps {
  embedded?: boolean;
}

export const ChatWidget = ({ embedded = false }: ChatWidgetProps) => {
  const [isOpen, setIsOpen] = useState(embedded); // Start open if embedded
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const addMessage = useCallback(
    (
      text: string,
      isUser: boolean,
      properties?: ChatProperty[],
      filters?: any
    ) => {
      const newMessage: Message = {
        id: Date.now().toString(),
        text,
        isUser,
        timestamp: new Date(),
        properties,
        filters,
      };
      setMessages((prev) => [...prev, newMessage]);
    },
    []
  );

  const sendHeartbeat = async () => {
    if (!sessionId) return;

    try {
      await fetch(`${CHAT_API}/chat/heartbeat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: sessionId }),
      });
    } catch (error) {
      console.error("Error sending heartbeat:", error);
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    if (!isOpen && !embedded) return;

    // Send heartbeat immediately when session is established
    sendHeartbeat();

    const interval = setInterval(() => {
      sendHeartbeat();
    }, CHAT_HEARTBEAT_INTERVAL);

    return () => clearInterval(interval);
  }, [isOpen, embedded, sessionId]);

  const sendMessage = async (messageText: string) => {
    // Add user message
    addMessage(messageText, true);
    setIsLoading(true);

    try {
      const requestBody: any = {
        message: messageText,
      };

      if (sessionId) {
        requestBody.session_id = sessionId;
      }

      const response = await fetch(`${CHAT_API}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }

      if (data.assistant_text) {
        addMessage(
          data.assistant_text,
          false,
          data.properties || [],
          data.filters || undefined
        );
      }
    } catch (error) {
      console.error("Error sending message:", error);
      addMessage(
        "Sorry, I encountered an error connecting to our services. Please try again.",
        false
      );
    } finally {
      setIsLoading(false);
    }
  };

  const sendVoiceMessage = async (audioBlob: Blob) => {
    setIsLoading(true);

    try {
      // Create FormData with audio file and session_id
      const formData = new FormData();
      formData.append("audio_file", audioBlob, "recording.wav");
      formData.append("session_id", sessionId || "");

      // Call the voice chat API
      const response = await fetch(`${CHAT_API}/chat/voice`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Store session_id from first response for future requests
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }

      // Add user message from transcribed text
      if (data.transcribed_text) {
        addMessage(data.transcribed_text, true);
      }

      // Add assistant response with properties if available
      if (data.assistant_text) {
        addMessage(
          data.assistant_text,
          false,
          data.properties || [],
          data.filters || undefined
        );
      }
    } catch (error) {
      console.error("Error sending voice message:", error);
      addMessage(
        "Sorry, I encountered an error processing your voice message. Please try again.",
        false
      );
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChat = () => {
    if (!embedded) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleClose = () => {
    if (!embedded) {
      setIsOpen(false);
    } else {
      // Send message to parent window (for iframe embedding)
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'BUBBLEMARBLE_CLOSE_CHAT' }, '*');
      }
    }
  };

  return (
    <>
      {/* Chat Button - only show if not embedded and chat is closed */}
      {!embedded && !isOpen && (
        <button
          onClick={toggleChat}
          className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-[#D14081] to-[#2A1745] text-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-[#2A1745] focus:ring-offset-2"
          aria-label="Open chat"
          aria-expanded={isOpen}
        >
          <BotAvatar />
        </button>
      )}

      {/* Chat Window - show if open OR if in embedded mode */}
      {(isOpen || embedded) && (
        <ChatWindow
          isOpen={isOpen || embedded}
          onClose={handleClose}
          messages={messages}
          onSendMessage={sendMessage}
          onSendVoiceMessage={sendVoiceMessage}
          isLoading={isLoading}
          embedded={embedded}
        />
      )}
    </>
  );
};

